import React from 'react';
import { wrapReactRouterRouting } from '@sentry/react/react-router';
import { Route, Routes, useLocation } from 'react-router';

import { useSelector, useDispatch } from 'hooks';
import { getUrlParam } from 'utils/urlUtils';
import useInitAnalytics from 'hooks/useInitAnalytics';

import { DEFAULT_THEME } from 'constants/index';
import { HOME, HOME_OUTDOOR_SHAREBOX } from 'constants/ErrorPage';

import { isModalOpen } from 'modules/healthCheck/selectors';
import { getAppTheme, isLowPerfMode } from 'modules/app/selectors';
import { getAdminToken, getQRCodeToken } from 'modules/auth/selectors';
import { dismissMobilityWarning } from 'modules/auth/actions';
import { hasShareboxStyle, isRemoteKiosk } from 'modules/dealers/selectors';
import { isKioskOffline as isKioskOfflineSelector } from 'modules/kiosk/selectors';
import { canReopenKiosk as canReopenKioskSelector, isKioskNotPrepared } from 'modules/selfServices/selectors';
import { hasRestrictedAccess as hasRestrictedAccessSelector, isRestrictedModalOpen as isRestrictedModalOpenSelector } from 'modules/restrictedAccess/selectors';
import { closeRestrictedAccessModal, continueRestrictedAccessModal } from 'modules/restrictedAccess/actions';

import Home from 'components/Home';
import Admin from 'components/Admin';
import Gates from 'components/Gates';
import Login from 'components/Login';
import Modal from 'components/ui/Modal';
import Cookies from 'components/Cookies';
import Slots from 'components/Admin/Slots';
import ErrorPage from 'components/Errors/Error';
import ReopenKiosk from 'components/ReopenKiosk';
import AdminLayout from 'components/Admin/Layout';
import VehicleCheck from 'components/VehicleCheck';
import AdminLogin from 'components/Admin/AdminLogin';
import Management from 'components/Admin/Management';
import OfflineModal from 'components/ui/OfflineModal';
import MobilityNotPreparedModal from 'components/Login/MobilityNotPreparedModal';
import RestrictedAccessModal from 'components/RestrictedAccessModal';
import SelfServiceLayout from 'components/SelfServices';
import KioskLocationId from 'components/KioskLocationId';
import FinalInstructions from 'components/FinalInstructions';
import StepContent from 'components/SelfServices/StepContent';
import LoginStepContent from 'components/Login/LoginStepContent';
import HomeOutdoorSharebox from 'components/HomeOutdoorSharebox';
import GateSuccess from 'components/Gates/components/GateSuccess';
import RemoteKioskError from 'components/Errors/RemoteKioskError';
import VehicleCheckForm from 'components/VehicleCheck/VehicleCheckForm';
import ReopenStepContent from 'components/ReopenKiosk/ReopenStepContent';
import PaymentLanding, { PaymentStatus } from 'components/PaymentCompleted';
import { getLocaleUsage } from 'constants/LanguageConstants';
import { getSelectedLanguage } from 'modules/translations/selectors';

import './refreshData';
import ShareboxEmergencyAccess from '../components/ui/ShareboxEmergencyAccess';
import Redirect from 'components/Redirect';
import Confirmation from 'components/Confirmation';
import { CONFIRMATION_BASE_URL } from 'constants/url';

const SentryRoutes = wrapReactRouterRouting(Routes);

const getThemeStylesheetPath = (theme: string) => {
  // @ts-ignore
  const HASH = __webpack_hash__;

  const iconName = theme === DEFAULT_THEME ? 'icon' : theme.toLowerCase();
  return `${process.env.ASSETS_URL}/public/themes/${theme.toLowerCase()}/font-icons/fb-${iconName}.css?t=${HASH}`;
};

const Router: React.FC = () => {
  useInitAnalytics();

  const location = useLocation();
  const dispatch = useDispatch();

  const token = getUrlParam('token');
  const outdoorShareboxToken = getUrlParam('OSToken');

  const theme = useSelector(getAppTheme);
  const isRemote = useSelector(isRemoteKiosk);
  const adminToken = useSelector(getAdminToken);
  const lowPerfMode = useSelector(isLowPerfMode);
  const qrCodeToken = useSelector(getQRCodeToken);
  const locale = useSelector(getSelectedLanguage);
  const shareboxStyle = useSelector(hasShareboxStyle);
  const isOfflineModalOpen = useSelector(isModalOpen);
  const kioskNotPrepared = useSelector(isKioskNotPrepared);
  const isKioskOffline = useSelector(isKioskOfflineSelector);
  const canReopenKiosk = useSelector(canReopenKioskSelector);
  const hasRestrictedAccess = useSelector(hasRestrictedAccessSelector);
  const isRestrictedModalOpen = useSelector(isRestrictedModalOpenSelector);

  const [displayMobilityWarning, setDisplayMobilityWarning] = React.useState(false);
  const canDisplayGateModal = hasRestrictedAccess && isRemote;

  React.useEffect(() => {
    if (kioskNotPrepared) {
      setDisplayMobilityWarning(kioskNotPrepared);
    }
  }, [kioskNotPrepared]);

  const closeMobilityWarning = () => setDisplayMobilityWarning(false);

  const handleOnCancel = () =>{
    closeMobilityWarning();
  };

  const handleCloseMobilityWarning = React.useCallback(() => {
    closeMobilityWarning();
    dispatch(dismissMobilityWarning());
  }, [dispatch]);

  const handleHideRestrictedAccessModal = React.useCallback(
    (shouldOpenGate: boolean) => dispatch(shouldOpenGate ? continueRestrictedAccessModal() : closeRestrictedAccessModal()),
    [dispatch],
  );

  React.useEffect(() => {
    document.documentElement.lang = getLocaleUsage(locale);
  }, [locale]);

  const publicRoutes = !token && !outdoorShareboxToken;
  const displayRemoteKioskError = isRemote && isKioskOffline;
  const displayOfflineModal = isOfflineModalOpen && location.pathname !== '/'; // hide the modal on the home page

  React.useLayoutEffect(() => {
    if (shareboxStyle || outdoorShareboxToken) {
      document.body.classList.add('no-cursor');
    }
    if (lowPerfMode) {
      document.body.classList.add('no-transition', 'no-blur');
    }
  }, [lowPerfMode, shareboxStyle, outdoorShareboxToken]);

  return (
    <>
      <link rel="stylesheet" href={getThemeStylesheetPath(theme)} precedence="theme" />
      <ShareboxEmergencyAccess />
      {displayRemoteKioskError && (
        <Modal open hideCloseButton className="modal-large bg-input-bg!">
          <RemoteKioskError />
        </Modal>
      )}

      {token && (
        <>
          <Cookies />
          {displayOfflineModal && <OfflineModal />}
          {displayMobilityWarning && <MobilityNotPreparedModal onClose={handleCloseMobilityWarning} onCancel={handleOnCancel}/>}
          {canDisplayGateModal && <RestrictedAccessModal open={isRestrictedModalOpen} onClose={handleHideRestrictedAccessModal} />}
          <SentryRoutes>
            <Route path="/">
              <Route index element={<Home />} />
              <Route path="login" element={<Login />}>
                <Route path=":step" element={<LoginStepContent />} />
              </Route>
              <Route path="checkin" element={<SelfServiceLayout />}>
                <Route path=":step" element={<StepContent />} />
              </Route>
              <Route path="checkout" element={<SelfServiceLayout />}>
                <Route path=":step" element={<StepContent />} />
              </Route>
              <Route path="wrongKiosk" element={<KioskLocationId />} />
              <Route path="breakdown" element={<SelfServiceLayout />}>
                <Route path=":step" element={<StepContent />} />
              </Route>
              {canReopenKiosk && (
                <Route path="reopen" element={<ReopenKiosk />}>
                  <Route path=":step" element={<ReopenStepContent />} />
                </Route>
              )}
              {qrCodeToken && <Route path="admin/login" element={<AdminLogin />} />}
              {adminToken && (
                <Route path="admin" element={<AdminLayout />}>
                  <Route index element={<Admin />} />
                  <Route path="slots" element={<Slots />} />
                  <Route path="management/:type" element={<Management />} />
                </Route>
              )}
              <Route path="vehicle-check">
                <Route index element={<VehicleCheck />} />
                <Route path=":type" element={<VehicleCheckForm />} />
                <Route path="final-instructions/:selfServiceId" element={<FinalInstructions />} />
              </Route>
              <Route path="gates">
                <Route index element={<Gates />} />
                <Route path="success" element={<GateSuccess />} />
              </Route>
            </Route>
            <Route path="*" element={<ErrorPage target={HOME} />} />
          </SentryRoutes>
        </>
      )}
      {outdoorShareboxToken && (
        <Routes>
          <Route path="/QRCode" element={<HomeOutdoorSharebox />} />
          <Route path="*" element={<ErrorPage target={HOME_OUTDOOR_SHAREBOX} />} />
        </Routes>
      )}
      {publicRoutes && (
        <Routes>
          <Route path="/redirect" element={<Redirect />} />
          <Route path={CONFIRMATION_BASE_URL} element={<Confirmation />} />
          <Route path="/payment-success" element={<PaymentLanding status={PaymentStatus.SUCCESS} />} />
          <Route path="/payment-failure" element={<PaymentLanding status={PaymentStatus.FAILURE} />} />
          <Route path="*" element={<ErrorPage />} />
        </Routes>
      )}
    </>
  );
};

export default Router;
